import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, 
  Mail, 
  MapPin, 
  MessageSquare, 
  Send,
  CheckCircle2,
  Building2,
  Users,
  Briefcase
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";

export default function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      // Still show success since we want to capture inquiries
      setSubmitted(true);
      toast.success("Message received! We'll get back to you soon.");
    } finally {
      setLoading(false);
    }
  };

  const contactMethods = [
    {
      icon: Mail,
      title: "Email Us",
      description: "For general inquiries and support",
      contact: "hello@expertbase.ai",
      href: "mailto:hello@expertbase.ai"
    },
    {
      icon: Building2,
      title: "Enterprise Sales",
      description: "For enterprise and GTM team building",
      contact: "enterprise@expertbase.ai",
      href: "mailto:enterprise@expertbase.ai"
    },
    {
      icon: MapPin,
      title: "Visit Us",
      description: "Our headquarters",
      contact: "Dubai, UAE",
      href: "#"
    }
  ];

  const faqs = [
    {
      question: "How do I become an expert on ExpertBase?",
      answer: "Click 'Become an Expert' in the navigation, fill out your profile with your expertise, experience, and set your hourly rate. Our team will review your application within 48 hours."
    },
    {
      question: "How do payments work?",
      answer: "All payments are processed securely through Stripe. Users pay when booking a session, and experts receive payouts according to the payout schedule (typically within 7 days after a completed session)."
    },
    {
      question: "What is the GTM Team Building service?",
      answer: "Our GTM (Go-to-Market) Team Building service helps companies expanding into new markets build their local teams. We leverage our expert network to identify and hire talent for executive, commercial, and operational roles."
    },
    {
      question: "Can I stay anonymous as an expert?",
      answer: "Yes! When creating your expert profile, you can select 'Stay Anonymous' which hides your name, photo, and company details from public view while still displaying your expertise and rates."
    }
  ];

  return (
    <div className="min-h-screen bg-obsidian">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <h1 className="font-display text-5xl md:text-6xl mb-6">Get in Touch</h1>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Have a question, feedback, or want to learn more about our services? 
              We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {contactMethods.map((method, index) => (
              <motion.a
                key={index}
                href={method.href}
                className="p-8 bg-charcoal border border-slate-800 rounded-sm hover:border-gold-500/50 transition-colors"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <method.icon className="w-10 h-10 text-gold-500 mb-4" />
                <h3 className="font-display text-xl mb-2">{method.title}</h3>
                <p className="text-slate-500 text-sm mb-3">{method.description}</p>
                <p className="text-gold-500">{method.contact}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-display text-3xl mb-6">Send Us a Message</h2>
              <p className="text-slate-400 mb-8">
                Fill out the form and our team will get back to you within 24 hours.
              </p>

              {submitted ? (
                <div className="bg-charcoal border border-slate-800 p-12 rounded-sm text-center">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="font-display text-2xl mb-4">Message Sent!</h3>
                  <p className="text-slate-400 mb-6">
                    Thank you for reaching out. We'll get back to you within 24 hours.
                  </p>
                  <Button 
                    onClick={() => setSubmitted(false)}
                    variant="outline"
                    className="border-slate-700"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Your Name *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="bg-charcoal border-slate-700"
                        placeholder="John Doe"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email Address *</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="bg-charcoal border-slate-700"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Subject</Label>
                    <Select 
                      value={formData.subject}
                      onValueChange={(v) => setFormData(prev => ({ ...prev, subject: v }))}
                    >
                      <SelectTrigger className="bg-charcoal border-slate-700">
                        <SelectValue placeholder="Select a topic" />
                      </SelectTrigger>
                      <SelectContent className="bg-charcoal border-slate-700">
                        <SelectItem value="general">General Inquiry</SelectItem>
                        <SelectItem value="support">Technical Support</SelectItem>
                        <SelectItem value="expert">Becoming an Expert</SelectItem>
                        <SelectItem value="enterprise">Enterprise / GTM Services</SelectItem>
                        <SelectItem value="partnership">Partnership Opportunities</SelectItem>
                        <SelectItem value="feedback">Feedback / Suggestions</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Message *</Label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      className="bg-charcoal border-slate-700 min-h-[150px]"
                      placeholder="How can we help you?"
                    />
                  </div>

                  <Button 
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gold-500 hover:bg-gold-400 text-black font-semibold py-6 rounded-sm"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              )}
            </motion.div>

            {/* FAQs */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-display text-3xl mb-6">Frequently Asked Questions</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div 
                    key={index}
                    className="bg-charcoal border border-slate-800 p-6 rounded-sm"
                  >
                    <h3 className="font-semibold text-white mb-2">{faq.question}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 bg-gold-500/10 border border-gold-500/30 rounded-sm">
                <h3 className="font-semibold text-gold-500 mb-2">Need Immediate Help?</h3>
                <p className="text-slate-400 text-sm mb-4">
                  For urgent matters, reach out directly to our team.
                </p>
                <a href="mailto:support@expertbase.ai" className="text-gold-500 hover:text-gold-400 text-sm font-medium">
                  support@expertbase.ai →
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
